import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRunLogs } from 'app/context/RunLogsContext';
import { testPlanDay } from 'app/apiService/nurturingPlan/nurturingPlanExecutionApiService';
import RunLogsSocketService from 'app/socketService/runLogs/runLogsSocketService';

type GenerationSection = {
  index: number;
  type: string;
  main?: string;
  sub_text?: string;
};

const JOB_PROGRESS_SECTIONS: GenerationSection[] = [
  { index: 1, type: 'status', main: 'Queued', sub_text: 'Test plan day queued' },
  { index: 2, type: 'status', main: 'Processing', sub_text: 'Nurturing candidates picker running' },
  { index: 3, type: 'status', main: 'Completed', sub_text: 'Test plan day completed' },
];

const RUN_PROGRESS_SECTIONS: GenerationSection[] = [
  { index: 1, type: 'status', main: 'Run started', sub_text: 'Run created for plan day test' },
  { index: 2, type: 'status', main: 'Running', sub_text: 'Processing nurturing candidates' },
  { index: 3, type: 'status', main: 'Completed', sub_text: 'Run finished' },
];

const getJobPayload = (data: any) => data?.message ?? data?.data?.message ?? data;

const mapJobStatusToSection = (status?: string): number => {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'queued') return 1;
  if (
    normalized === 'processing' ||
    normalized === 'running' ||
    normalized === 'pending' ||
    normalized === 'deferred' ||
    normalized === 'retrying'
  ) {
    return 2;
  }
  if (normalized === 'completed' || normalized === 'success') return 3;
  if (normalized === 'failed' || normalized === 'error') return 2;
  return 1;
};

const mapRunStatusToSection = (status?: string): number => {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'pending' || normalized === 'queued') return 1;
  if (normalized === 'running' || normalized === 'processing') return 2;
  if (normalized === 'completed' || normalized === 'success') return 3;
  if (normalized === 'failed' || normalized === 'error') return 2;
  return 1;
};

const isTerminalStatus = (status?: string): boolean => {
  const normalized = String(status || '').toLowerCase();
  return (
    normalized === 'completed' ||
    normalized === 'success' ||
    normalized === 'failed' ||
    normalized === 'error'
  );
};

const getRunLogsSocketService = (): RunLogsSocketService | null => {
  try {
    return RunLogsSocketService.getInstance();
  } catch (_error) {
    try {
      return RunLogsSocketService.getInstance({
        url: process.env.REACT_APP_API_URL || 'http://localhost:3000',
        autoConnect: true,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      });
    } catch (_innerError) {
      return null;
    }
  }
};

export type UseNurturingPlanExecutionTestProgressOptions = {
  dealerId?: string;
  onComplete?: () => void;
  onError?: (message: string) => void;
};

export function useNurturingPlanExecutionTestProgress(
  options: UseNurturingPlanExecutionTestProgressOptions,
) {
  const { dealerId, onComplete, onError } = options;
  const {
    subscribeToRunLog = () => {},
    unsubscribeFromRunLog = () => {},
    subscribeToJob = () => {},
    unsubscribeFromJob = () => {},
    realTimeRunLogs = [],
  } = useRunLogs() || {};

  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [activePlanDay, setActivePlanDay] = useState<number | null>(null);
  const [isTracking, setIsTracking] = useState(false);
  const [isFinal, setIsFinal] = useState(false);
  const [phase, setPhase] = useState<'idle' | 'job' | 'run'>('idle');
  const [currentSection, setCurrentSection] = useState<GenerationSection | undefined>(
    JOB_PROGRESS_SECTIONS[0],
  );
  const [progressMessage, setProgressMessage] = useState('Waiting to start...');
  const [latestJobPayload, setLatestJobPayload] = useState<any>(null);
  const [latestRunEvent, setLatestRunEvent] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  const completedRef = useRef(false);
  const activeJobIdRef = useRef<string | null>(null);
  const activeRunIdRef = useRef<string | null>(null);

  useEffect(() => {
    activeJobIdRef.current = activeJobId;
  }, [activeJobId]);

  useEffect(() => {
    activeRunIdRef.current = activeRunId;
  }, [activeRunId]);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const progressSections = phase === 'run' ? RUN_PROGRESS_SECTIONS : JOB_PROGRESS_SECTIONS;

  const progressOverride = useMemo(() => {
    if (!isTracking) return undefined;
    if (isFinal) return 100;
    if (phase === 'job') {
      const status = String(latestJobPayload?.status || '').toLowerCase();
      if (!status && activeJobId) return 10;
      if (status === 'completed') return 100;
      return mapJobStatusToSection(status) * 33;
    }
    const status = String(
      latestRunEvent?.status || latestRunEvent?.run_status || '',
    ).toLowerCase();
    if (status === 'completed' || status === 'success') return 100;
    return mapRunStatusToSection(status) * 33;
  }, [isTracking, isFinal, phase, latestJobPayload, latestRunEvent, activeJobId]);

  const resetProgress = useCallback(() => {
    const jobId = activeJobIdRef.current;
    const runId = activeRunIdRef.current;

    if (jobId) {
      unsubscribeFromJob(jobId);
    }
    if (runId) {
      unsubscribeFromRunLog(runId);
    }
    if (dealerId) {
      getRunLogsSocketService()?.unsubscribeFromDealer(dealerId);
    }

    activeJobIdRef.current = null;
    activeRunIdRef.current = null;
    completedRef.current = false;

    setActiveJobId(null);
    setActiveRunId(null);
    setActivePlanDay(null);
    setIsTracking(false);
    setIsFinal(false);
    setPhase('idle');
    setLatestJobPayload(null);
    setLatestRunEvent(null);
    setErrorMessage(null);
    setProgressMessage('Waiting to start...');
    setCurrentSection(JOB_PROGRESS_SECTIONS[0]);
  }, [dealerId, unsubscribeFromJob, unsubscribeFromRunLog]);

  const failProgress = useCallback(
    (message: string) => {
      onErrorRef.current?.(message);
      resetProgress();
    },
    [resetProgress],
  );

  const markComplete = useCallback((message: string) => {
    if (completedRef.current) return;
    completedRef.current = true;
    setIsFinal(true);
    setProgressMessage(message);
    setCurrentSection(
      phase === 'run' ? RUN_PROGRESS_SECTIONS[2] : JOB_PROGRESS_SECTIONS[2],
    );
    onCompleteRef.current?.();
  }, [phase]);

  const startTestPlanDay = useCallback(
    async (planDay: number) => {
      if (!dealerId) {
        throw new Error('Please select a dealer first');
      }

      const hasActiveSession =
        Boolean(activeJobIdRef.current) || isTracking || completedRef.current;

      if (hasActiveSession && !completedRef.current) {
        throw new Error('A plan day test is already in progress');
      }

      if (completedRef.current) {
        resetProgress();
      }

      completedRef.current = false;
      setErrorMessage(null);
      setIsFinal(false);
      setActiveRunId(null);
      setActivePlanDay(planDay);
      setLatestJobPayload(null);
      setLatestRunEvent(null);
      setCurrentSection(JOB_PROGRESS_SECTIONS[0]);
      setProgressMessage(`Queuing test for plan day ${planDay}...`);

      const response = await testPlanDay(dealerId, planDay);
      const jobId = String(response.job_id);

      setIsTracking(true);
      setPhase('job');
      activeJobIdRef.current = jobId;
      setActiveJobId(jobId);
      setProgressMessage(`Subscribed to job ${jobId}`);

      const socketService = getRunLogsSocketService();
      socketService?.connect();
      socketService?.subscribeToDealer(dealerId);
      subscribeToJob(jobId);
    },
    [dealerId, isTracking, resetProgress, subscribeToJob],
  );

  useEffect(() => {
    if (!dealerId || !isTracking) return;

    const socketService = getRunLogsSocketService();
    if (!socketService) return;

    const runCreatedEvent = `${dealerId}_run_created`;
    const handleRunCreated = (data: any) => {
      const message = data?.message ?? data;
      const runId = message?.run_id || message?.runId || message?.id;
      if (!runId) return;

      setActiveRunId(prev => {
        if (prev) return prev;
        const nextRunId = String(runId);
        activeRunIdRef.current = nextRunId;
        subscribeToRunLog(nextRunId);
        setPhase('run');
        setCurrentSection(RUN_PROGRESS_SECTIONS[0]);
        setProgressMessage(`Run created: ${nextRunId}`);
        return nextRunId;
      });
    };

    socketService.on(runCreatedEvent, handleRunCreated);
    return () => {
      socketService.off(runCreatedEvent, handleRunCreated);
    };
  }, [dealerId, isTracking, subscribeToRunLog]);

  useEffect(() => {
    if (!activeJobId || !isTracking) return;

    const socketService = getRunLogsSocketService();
    if (!socketService) return;

    const handler = (data: any) => {
      const payload = getJobPayload(data);
      const jobId = payload?.job_id || payload?.queued_job_id;
      if (!jobId || jobId !== activeJobId) return;

      const status = String(payload?.status || '').toLowerCase();
      const message = String(payload?.message || 'Test plan day job update received.');
      const section =
        JOB_PROGRESS_SECTIONS.find(s => s.index === mapJobStatusToSection(status)) ||
        JOB_PROGRESS_SECTIONS[0];

      setLatestJobPayload(payload);
      setCurrentSection(section);
      setProgressMessage(message);

      if (status === 'failed' || status === 'error') {
        failProgress(message);
      } else if (status === 'completed' || status === 'success') {
        if (activeRunIdRef.current) {
          setPhase('run');
          setProgressMessage(message);
        } else {
          markComplete(message);
          unsubscribeFromJob(activeJobId);
        }
      }
    };

    socketService.on('queue_job_lifecycle', handler);
    return () => {
      socketService.off('queue_job_lifecycle', handler);
    };
  }, [activeJobId, isTracking, failProgress, markComplete, unsubscribeFromJob]);

  useEffect(() => {
    if (!activeRunId || !isTracking) return;

    const matchingEvents = realTimeRunLogs.filter(log => {
      const logRunId = log.runId || log.run_log_id || log.id;
      return String(logRunId) === activeRunId;
    });
    if (!matchingEvents.length) return;

    const latest = matchingEvents[matchingEvents.length - 1];
    const status = String(latest.status || '').toLowerCase();
    const message = String(latest.message || 'Run update received.');
    const section =
      RUN_PROGRESS_SECTIONS.find(s => s.index === mapRunStatusToSection(status)) ||
      RUN_PROGRESS_SECTIONS[1];

    setLatestRunEvent(latest);
    setCurrentSection(section);
    setProgressMessage(message);

    if (!isTerminalStatus(status)) return;

    if (status === 'failed' || status === 'error') {
      failProgress(message);
      return;
    }

    markComplete(message);
    unsubscribeFromRunLog(activeRunId);
    if (activeJobId) {
      unsubscribeFromJob(activeJobId);
    }
  }, [
    activeRunId,
    activeJobId,
    isTracking,
    realTimeRunLogs,
    markComplete,
    failProgress,
    unsubscribeFromJob,
    unsubscribeFromRunLog,
  ]);

  useEffect(() => {
    return () => {
      const jobId = activeJobIdRef.current;
      const runId = activeRunIdRef.current;
      if (jobId) {
        unsubscribeFromJob(jobId);
      }
      if (runId) {
        unsubscribeFromRunLog(runId);
      }
      if (dealerId) {
        getRunLogsSocketService()?.unsubscribeFromDealer(dealerId);
      }
    };
  }, [dealerId, unsubscribeFromJob, unsubscribeFromRunLog]);

  return {
    activeJobId,
    activeRunId,
    activePlanDay,
    isTracking,
    isFinal,
    phase,
    progressSections,
    currentSection,
    progressMessage,
    progressOverride,
    latestJobPayload,
    latestRunEvent,
    errorMessage,
    startTestPlanDay,
    resetProgress,
  };
}
